// sep-2026 · Ajuste manual del cupo mensual de ALTAS de una entidad (solo super_admin).
// El super_admin negocia fuera del sistema y aquí registra el acuerdo. NO cobra nada y NO
// toca Stripe: solo mueve el límite que el backend aplica al bloquear altas nuevas.
// oct-2026 · Se captura el LÍMITE TOTAL de altas, ya no un incremento: el backend calcula
// los extra sobre lo incluido. Antes dos ajustes seguidos se sumaban y el total real
// terminaba distinto del número que el super_admin creía fijar.
//   · «Solo este mes»: total que aplica al mes en curso; al cerrar el mes vence.
//   · «Permanente»: total mensual que sustituye al del plan.
//   · «Regresar al límite del plan»: quita ambos ajustes.
// El motivo es obligatorio (≥10 caracteres) y queda en la bitácora de la entidad.
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { SlidersHorizontal, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const MIN_MOTIVO = 10;

export function AjustarAltasButton({ entidad, uso, onDone }) {
  const [abierto, setAbierto] = useState(false);
  const [tipo, setTipo] = useState("mes");
  const [cantidad, setCantidad] = useState("");
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  const cupoPlan = uso?.cupo_plan;
  const personalizado = uso?.limite_personalizado;
  const extra = uso?.extra_mes || 0;
  const totalActual = uso?.cupo;
  // Mínimo que acepta el backend: lo ya incluido (límite permanente vigente o el del plan).
  const incluidas = (personalizado !== null && personalizado !== undefined)
    ? personalizado : cupoPlan;
  const hayAjuste = (personalizado !== null && personalizado !== undefined) || extra > 0;

  const abrir = (e) => {
    e.stopPropagation();
    setCantidad(totalActual >= 0 ? String(totalActual) : "");
    setAbierto(true);
  };

  const enviar = async (forzarTipo) => {
    const t = forzarTipo || tipo;
    if (motivo.trim().length < MIN_MOTIVO) {
      toast.error(`Escribe el motivo del ajuste (mínimo ${MIN_MOTIVO} caracteres): queda en la bitácora.`);
      return;
    }
    if (t !== "plan" && !(Number(cantidad) > 0)) {
      toast.error("Captura el límite total de altas del mes (mayor a 0).");
      return;
    }
    setEnviando(true);
    try {
      const { data } = await api.post(`/admin/entidades/${entidad.id}/ajustar-altas`, {
        tipo: t, cantidad: t === "plan" ? null : Number(cantidad), motivo: motivo.trim(),
      });
      toast.success(`Altas del mes: ${data.antes.limite} → ${data.despues.limite}. Queda en bitácora.`);
      setAbierto(false); setCantidad(""); setMotivo(""); setTipo("mes");
      onDone && onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEnviando(false); }
  };

  return (
    <>
      <Button data-testid={`ajustar-altas-${entidad.id}`} variant="outline" size="sm"
        className="h-8 gap-1.5 px-2.5 text-xs" onClick={abrir}>
        <SlidersHorizontal className="h-3.5 w-3.5" /> Ajustar altas
      </Button>

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="sm:max-w-lg" data-testid="ajustar-altas-dialogo">
          <DialogHeader>
            <DialogTitle>Ajustar altas del mes</DialogTitle>
            <DialogDescription>
              {entidad.nombre_comercial || entidad.razon_social || entidad.id}
              {uso ? ` · este mes ${uso.usadas}/${uso.cupo} altas` : ""}
              {cupoPlan >= 0 ? ` · plan: ${cupoPlan}` : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {[["mes", "Solo este mes", "Total que aplica al mes en curso; vence al cierre"],
                ["permanente", "Permanente", "Total mensual, sustituye al del plan"]].map(
                ([v, titulo, ayuda]) => (
                  <button key={v} type="button" data-testid={`ajuste-tipo-${v}`}
                    onClick={() => setTipo(v)}
                    className={`rounded-md border p-2.5 text-left transition-colors ${
                      tipo === v ? "border-primary bg-primary/5" : "border-slate-200 hover:bg-slate-50"}`}>
                    <p className="text-xs font-semibold text-slate-800">{titulo}</p>
                    <p className="mt-0.5 text-[10px] leading-snug text-slate-500">{ayuda}</p>
                  </button>
                ))}
            </div>

            <div>
              <Label className="text-xs">
                {tipo === "mes" ? "Límite TOTAL de altas para este mes"
                  : "Nuevo límite TOTAL mensual de altas"}
              </Label>
              <Input data-testid="ajuste-cantidad" type="number" min="1" value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
                placeholder={tipo === "mes" ? "200" : "400"} className="mt-1" />
              <p className="mt-1 text-[10px] leading-snug text-slate-500">
                Es el total, no un incremento: el sistema calcula los extra sobre las{" "}
                {incluidas >= 0 ? incluidas : "—"} altas ya incluidas.
                {totalActual >= 0 ? ` Hoy el límite del mes es ${totalActual}.` : ""}
                {tipo === "mes" && incluidas >= 0
                  ? ` El mínimo que acepta es ${incluidas} (lo incluido).` : ""}
              </p>
            </div>

            <div>
              <Label className="text-xs">Motivo del ajuste (obligatorio)</Label>
              <Textarea data-testid="ajuste-motivo" value={motivo} rows={3}
                onChange={(e) => setMotivo(e.target.value)} className="mt-1"
                placeholder="Ej.: acuerdo comercial con el director de la SOFOM por campaña de nómina de octubre." />
              <p className="mt-1 text-[10px] text-slate-500">
                Mínimo {MIN_MOTIVO} caracteres. Queda en la bitácora de la entidad con el
                límite anterior y el nuevo. ({motivo.trim().length})
              </p>
            </div>

            {hayAjuste && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
                <p className="text-[11px] text-slate-600">
                  Ajuste vigente:{" "}
                  {personalizado !== null && personalizado !== undefined
                    ? <b>límite permanente de {personalizado} altas</b>
                    : <b>límite total de {totalActual} altas este mes</b>}
                  {cupoPlan >= 0 ? ` (el plan da ${cupoPlan}).` : "."}
                </p>
                <Button data-testid="ajuste-regresar-plan" variant="outline" size="sm"
                  className="mt-2 h-7 gap-1.5 px-2 text-[11px]" disabled={enviando}
                  onClick={() => enviar("plan")}>
                  <RotateCcw className="h-3 w-3" /> Regresar al límite del plan
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAbierto(false)} disabled={enviando}>Cerrar</Button>
            <Button data-testid="ajuste-guardar" onClick={() => enviar()} disabled={enviando}>
              {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tipo === "mes" ? "Fijar límite de este mes" : "Fijar límite permanente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AjustarAltasButton;
